<template>
  <div class="community-setting-manage-container">
    <el-row :gutter="20">
      <el-col :span="4">
        <el-card class="setting-type-card">
          <div class="setting-type-list">
            <div v-for="(item, index) in settingTypes" :key="index" class="setting-type-item"
              :class="{ 'active': conditions.settingType === item.statusCd }" @click="handleSwitchSettingType(item)">
              {{ item.name }}
            </div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="20">
        <el-card>
          <div slot="header" class="flex justify-between">
            <span>{{ currentSettingName }}</span>
          </div>
          <div class="setting-content text-left">
            <el-form label-position="right" label-width="150px">
              <el-form-item v-for="(item, index) in settingKeys" :key="index" :label="item.settingName">

                <!-- 开关按钮：欠费限门禁开关 -->
                <el-switch v-if="isSwitchKey(item.settingKey)"
                  v-model="item._switchValue"
                  active-text="ON"
                  inactive-text="OFF"
                  active-color="#13ce66"
                  inactive-color="#ff4949"
                  @change="onSwitchChange(item)" />

                <!-- 计数器：费用到期提醒天数 -->
                <div class="number-with-suffix" v-else-if="isNumberKey(item.settingKey)">
                  <el-input-number
                    v-model="item._numberValue"
                    :min="1"
                    :max="365"
                    :step="1"
                    step-strictly
                    @change="onNumberChange(item)" />
                  <span class="number-suffix">天</span>
                </div>

                <!-- 模板：文本域 -->
                <el-input v-else-if="isTemplateKey(item.settingKey)"
                  v-model="item.settingValue"
                  type="textarea"
                  :rows="3"
                  :placeholder="$t('communitySettingManage.inputPlaceholder', { name: item.settingName })" />

                <!-- 普通文本 -->
                <el-input v-else
                  v-model="item.settingValue"
                  :placeholder="$t('communitySettingManage.inputPlaceholder', { name: item.settingName })" />

                <div class="setting-remark">{{ item.remark }}</div>
              </el-form-item>
            </el-form>
            <div class="setting-tips">{{ $t('communitySettingManage.settingTips') }}</div>
            <div class="text-right">
              <el-button type="primary" @click="handleSaveSettings">
                <i class="el-icon-check"></i>
                {{ $t('common.submit') }}
              </el-button>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import { getCommunityId } from '@/api/community/communityApi'
import { getDict } from '@/api/community/communityApi'
import {
  listCommunitySettingKey,
  saveCommunitySetting
} from '@/api/system/communitySettingManageApi'

// 开关类型设置键
const SWITCH_KEYS = ['FEE_LINK_IOT', 'FEE_OWE_REMIND_IOT']
// 计数器（数字输入）类型设置键
const NUMBER_KEYS = ['FEE_EXPIRE_REMIND']
// 模板（文本域）类型设置键
const TEMPLATE_KEYS = ['FEE_EXPIRE_REMIND_TEMPLATE', 'FEE_OWE_REMIND_TEMPLATE']
// 模板默认值
const TEMPLATE_DEFAULTS = {
  FEE_EXPIRE_REMIND_TEMPLATE: '尊敬的业主！您{floor}栋{unit}单元{room}房屋有{feeType}费用{days}天后到期，请尽快缴费!',
  FEE_OWE_REMIND_TEMPLATE: '{feeType}费用已欠费，请尽快缴费延期!'
}

export default {
  name: 'CommunitySettingManageList',
  data() {
    return {
      communityId: '',
      settingTypes: [],
      settingKeys: [],
      currentSettingName: '',
      conditions: {
        settingType: '',
        communityId: ''
      }
    }
  },
  created() {
    this.communityId = getCommunityId()
    this.conditions.communityId = this.communityId
    this.initData()
  },
  methods: {
    async initData() {
      try {
        const data = await getDict('community_setting_key', 'setting_type')
        this.settingTypes = data
        if (data && data.length > 0) {
          this.handleSwitchSettingType(data[0])
        }
      } catch (error) {
        console.error('获取字典数据失败:', error)
      }
    },
    async handleSwitchSettingType(item) {
      this.conditions.settingType = item.statusCd
      this.currentSettingName = item.name
      await this.loadSettingKeys()
    },
    // 判断是否为开关类型
    isSwitchKey(settingKey) {
      return SWITCH_KEYS.includes(settingKey)
    },
    // 判断是否为计数器类型
    isNumberKey(settingKey) {
      return NUMBER_KEYS.includes(settingKey)
    },
    // 判断是否为模板类型
    isTemplateKey(settingKey) {
      return TEMPLATE_KEYS.includes(settingKey)
    },
    // 开关值变更同步到 settingValue
    onSwitchChange(item) {
      item.settingValue = item._switchValue ? 'ON' : 'OFF'
    },
    // 计数器值变更同步到 settingValue
    onNumberChange(item) {
      item.settingValue = String(item._numberValue)
    },
    async loadSettingKeys() {
      try {
        const params = {
          communityId: this.communityId,
          settingType: this.conditions.settingType
        }
        const { data } = await listCommunitySettingKey(params)
        this.settingKeys = data.map(item => {
          let result = { ...item, settingValue: item.settingValue || '' }
          // 开关类型：ON → true, 其他 → false
          if (SWITCH_KEYS.includes(item.settingKey)) {
            result._switchValue = item.settingValue === 'ON'
          }
          // 计数器类型：字符串转数字，默认15
          if (NUMBER_KEYS.includes(item.settingKey)) {
            let num = parseInt(item.settingValue, 10)
            result._numberValue = isNaN(num) ? 15 : num
            result.settingValue = String(result._numberValue)
          }
          // 模板类型：空值时使用默认模板
          if (TEMPLATE_KEYS.includes(item.settingKey) && !result.settingValue) {
            result.settingValue = TEMPLATE_DEFAULTS[item.settingKey] || ''
          }
          return result
        })
      } catch (error) {
        console.error('获取设置项失败:', error)
      }
    },
    async handleSaveSettings() {
      try {
        // 保存前确保开关和计数器的值已同步到 settingValue
        this.settingKeys.forEach(item => {
          if (SWITCH_KEYS.includes(item.settingKey)) {
            item.settingValue = item._switchValue ? 'ON' : 'OFF'
          }
          if (NUMBER_KEYS.includes(item.settingKey)) {
            item.settingValue = String(item._numberValue)
          }
        })

        const params = {
          communityId: this.communityId,
          keys: this.settingKeys,
          settingType: this.conditions.settingType
        }
        const { code, msg } = await saveCommunitySetting(params)
        if (code != 0) {
          this.$message.error(msg)
          return;
        }
        this.$message.success(this.$t('common.operationSuccess'))
        this.loadSettingKeys()
      } catch (error) {
        console.error('保存设置失败:', error)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.community-setting-manage-container {
  padding: 20px;

  .setting-type-card {
    height: 100%;

    .setting-type-list {
      .setting-type-item {
        padding: 10px;
        margin-bottom: 5px;
        cursor: pointer;
        border-radius: 4px;
        text-align: center;

        &:hover {
          background-color: #f5f7fa;
        }

        &.active {
          background-color: #409eff;
          color: white;
        }
      }
    }
  }

  .setting-content {
    padding: 20px;

    .setting-remark {
      margin-top: 5px;
      font-size: 12px;
      color: #909399;
    }

    .number-with-suffix {
      display: inline-flex;
      align-items: center;
    }
    .number-suffix {
      margin-left: 8px;
      font-size: 14px;
      color: #606266;
    }
    .setting-tips {
      margin: 20px 0;
      font-size: 12px;
      color: #f56c6c;
    }
  }
}
</style>